import { useMutation } from '@apollo/client/react';
import { Fragment, useState, useTransition } from 'react';
import { CircleAlert } from 'lucide-react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { EscrowTerminationRequestDocument } from 'src/graphql/actions/escrow/escrowTerminationRequest.mutation';
import { getApiError, ClientError } from 'src/utils/errors';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import { refetchAppProtections } from 'src/containers/Applications/Protections/api';

const RequestTerminationDialog = () => {
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [isPending, startTransition] = useTransition();
  const {
    softwareEscrow: { id: escrowId },
    ui: { isTerminationDialogOpen },
    refetch,
    setIsTerminationDialogOpen,
  } = useSoftwareEscrow();
  const [requestEscrowTermination, { loading }] = useMutation(EscrowTerminationRequestDocument, {
    onCompleted: refetchAppProtections,
  });

  const onRequestTermination = async () => {
    try {
      const { data } = await requestEscrowTermination({ variables: { escrowId } });
      if (data?.escrowTerminationRequest?.success) {
        setIsTerminationDialogOpen(false);
        setIsSuccessOpen(true);
      } else {
        throw new ClientError(data?.escrowTerminationRequest?.errors?.[0]);
      }
    } catch (error: unknown) {
      getApiError(error, toast.error);
    }
  };

  const onSuccessOpenChange = (open: boolean) =>
    startTransition(async () => {
      if (!open) {
        await refetch();
        setIsSuccessOpen(false);
      }
    });

  return (
    <Fragment>
      <AlertDialog open={isTerminationDialogOpen} onOpenChange={setIsTerminationDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to request termination of this escrow?</AlertDialogTitle>
            <AlertDialogDescription>
              Your termination request will be sent to your counterpart for approval. If approved, the escrow will be
              terminated as outlined in the agreement.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 text-card-foreground text-sm">
              <CircleAlert className="size-3.5 text-orange-700 shrink-0" />
              <span className="font-semibold">For executed agreements</span>
            </div>
            <AlertDialogDescription>
              For executed agreements, the legal notice period in the agreement will apply.
            </AlertDialogDescription>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button variant="destructive" onClick={onRequestTermination} isLoading={loading}>
              Request termination
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={isSuccessOpen} onOpenChange={onSuccessOpenChange}>
        <DialogContent className="sm:max-w-[441px]">
          <DialogHeader className="text-center">
            <DialogTitle className="text-lg">Escrow termination request submitted</DialogTitle>
            <DialogDescription>
              Your counterpart will receive your request and can accept or reject it. We’ll notify you of their decision
              once the review is complete.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button className="w-full" isLoading={isPending} disabled={isPending}>
                Back to escrow
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Fragment>
  );
};

export default RequestTerminationDialog;
